import { useCallback } from 'react';
import {
  useWithdrawCard,
  useWithdrawCardModals,
  type WithdrawCardModalState,
} from '@/features/withdraw-card';
import { useTranslation } from 'react-i18next';
import type {
  TwoFAParams,
  WithdrawCardBodyRequest,
  WithdrawCardErrorResponse,
  WithdrawCommissionData,
  WithdrawSuccessResponse,
} from '@/shared/types';
import type { InfoDataGridData } from '@/shared/components';
import { WithdrawConfirmationModal } from '@/shared/components';
import { maskCardNumber } from '@/shared/utils';
import { useWithdrawMFAFlow } from '@/shared/lib';
import { useAfterOperationRefresh } from '@/entities/operation';

interface WithdrawConfirmationModalContainerProps {
  onBack: () => void;
  storeData: WithdrawCardModalState;
  commissionData: WithdrawCommissionData;
  continueMFA?: boolean;
}

export const WithdrawConfirmationModalContainer = ({
  onBack,
  storeData,
  commissionData,
  continueMFA,
}: WithdrawConfirmationModalContainerProps) => {
  const { t } = useTranslation();
  const { openResultModal, openConfirmationModal } = useWithdrawCardModals();

  const afterOperationRefresh = useAfterOperationRefresh();

  const currencyCode = storeData.selectedCurrency.code;
  const initialParams: WithdrawCardBodyRequest = {
    accountNumber: storeData.accountNumber,
    amount: storeData.totalAmount.toString(),
    currencyCode,
    userCardId: storeData.userCardId,
  };

  const injectMFA = useCallback(
    (
      params: WithdrawCardBodyRequest,
      mfa: TwoFAParams
    ): WithdrawCardBodyRequest => {
      return {
        ...params,
        twoFA: mfa,
      };
    },
    []
  );

  const { showMFA, startMFAVerification, renderMFAView } = useWithdrawMFAFlow<
    WithdrawCardBodyRequest,
    WithdrawSuccessResponse,
    WithdrawCardErrorResponse
  >({
    initialParams,
    injectMFA,
    useMutationHook: useWithdrawCard,
    onSuccess: () => {
      openResultModal({ success: true });
      afterOperationRefresh();
    },
    onError: () => {
      openResultModal({ success: false });
    },
    onBack: openConfirmationModal,
    onClose: openConfirmationModal,
    onCancel: openConfirmationModal,
    onMFASetupComplete: () => {
      openConfirmationModal({ continueMFA: true });
    },
    modalTitle: 'withdrawMethods.crypto.confirmModal.title',
    continueMFA,
  });

  const onConfirmed = useCallback(() => {
    startMFAVerification();
  }, [startMFAVerification]);

  const addressInfo: InfoDataGridData = storeData
    ? [
        {
          key: t('withdrawMethods.card.asset'),
          value: currencyCode,
        },
        {
          key: t('withdrawMethods.card.toCard'),
          value: maskCardNumber(storeData.withdrawCardFormData.cardNumber),
        },
      ]
    : [];

  const amountsInfo: InfoDataGridData =
    storeData && commissionData
      ? [
          {
            key: t('withdrawMethods.amountInfo.totalWithdraw'),
            value: commissionData.totalWithdrawAmountStr,
          },
          {
            key: t('withdrawMethods.amountInfo.withdrawFee'),
            value: commissionData.feeStr,
          },
          {
            key: t('withdrawMethods.amountInfo.toBeReceived'),
            value: commissionData.toBeReceivedStr,
          },
        ]
      : [];

  if (showMFA) {
    return renderMFAView();
  }

  return (
    <WithdrawConfirmationModal
      title={t('withdrawMethods.crypto.confirmModal.title')}
      onBack={onBack}
      infoData={[addressInfo, amountsInfo]}
      onConfirmed={onConfirmed}
      isLoading={false}
      buttonText={t('withdrawMethods.withdrawButton')}
    />
  );
};
